import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { globalStyle } from "../../global/Utils";
import { InputField } from "../Common/InputField";
import { SearchableSelect } from "../Common/SearchableSelect";
import { upperCaseInput } from "../../global/textCase";
import { getItem } from "../../Actions/ItemAction";
import {
  ITEM_TYPE,
  isStockTracked,
  formatQty,
  roundQty,
  unitOf,
  itemStockState,
  stockChipStyle,
} from "../../global/stock";
import { round2, formatTwoDecimals } from "../../global/money";

/**
 * The receiving form, shared by the add and edit screens.
 *
 * Both are the same form over the same lines; only where the initial values
 * come from and what the submit button does differ, so those are props rather
 * than a second copy of the whole thing that would drift.
 */

const today = () => new Date().toISOString().split("T")[0];

// An item's company arrives populated from the list endpoint but is a bare id
// on anything that skipped the populate, so read it either way.
const companyIdOf = (item) => item?.companyId?._id || item?.companyId || "";

// unitPrice is stored as a string and is often blank, so only a real number is
// worth seeding the cost field with - "" or junk leaves it empty to type into.
const priceOf = (item) => {
  const price = Number(item?.unitPrice);
  return item?.unitPrice !== "" && Number.isFinite(price) ? String(price) : "";
};

let lineKeySeq = 0;
export const emptyLine = () => ({
  _key: `line-${lineKeySeq++}`,
  itemId: "",
  itemName: "",
  hsCode: "",
  uom: "",
  quantity: "1",
  unitCost: "",
});

export const blankValues = () => ({
  receivingNumber: "",
  date: today(),
  supplierName: "",
  referenceNo: "",
  note: "",
  companyId: "",
  items: [emptyLine()],
});

const ReceivingForm = ({ initial, submitLabel, submitting, onSubmit }) => {
  const dispatch = useDispatch();

  const companiesList = useSelector(
    (state) => state.CompanyListReducer?.companiesList || [],
  );
  const itemList = useSelector((state) => state.ItemListReducer?.itemList || []);

  const [formData, setFormData] = useState(initial);
  const [errors, setErrors] = useState({});

  // Re-seed when the screen finishes loading the record it is editing.
  useEffect(() => {
    setFormData(initial);
  }, [initial]);

  useEffect(() => {
    // Empty page/limit means "the whole list" (listPaging in Utils.js), because
    // the picker filters client-side.
    dispatch(getItem("", "", ""));
  }, [dispatch]);

  /**
   * Only stock-tracked products may be received - receiving a service product
   * is meaningless, so one is never offered. The server rejects it as well, so
   * this is the convenience, not the rule.
   *
   * The company comes first, exactly as on the invoice forms: until one is
   * chosen nothing is offered, and after that only that company's items, so a
   * receiving can never be booked against another company's product.
   */
  const receivableItems = useMemo(
    () =>
      formData.companyId
        ? itemList.filter(
            (item) =>
              isStockTracked(item) &&
              companyIdOf(item) === formData.companyId,
          )
        : [],
    [itemList, formData.companyId],
  );

  const itemsById = useMemo(() => {
    const byId = {};
    itemList.forEach((item) => {
      byId[item._id] = item;
    });
    return byId;
  }, [itemList]);

  const takenIds = useMemo(
    () => new Set(formData.items.map((line) => line.itemId).filter(Boolean)),
    [formData.items],
  );

  /**
   * The list a single line may choose from.
   *
   * An item already sitting on another line is left off it entirely - a product
   * belongs on a receiving once, with one quantity, so offering it twice only
   * invites the duplicate the validator would then reject. The line's own item
   * stays on its own list, obviously, and so does an item that has since moved
   * company or stopped being stock-tracked: opening a saved receiving to change
   * a quantity must not silently blank the item it was booked against.
   */
  const optionsFor = (itemId) => {
    const available = receivableItems.filter(
      (item) => item._id === itemId || !takenIds.has(item._id),
    );
    const current = itemsById[itemId];
    if (!current || available.some((item) => item._id === itemId)) {
      return available;
    }
    return [current, ...available];
  };

  const totals = useMemo(() => {
    let quantity = 0;
    let cost = 0;
    formData.items.forEach((line) => {
      const qty = Number(line.quantity) || 0;
      quantity += qty;
      cost += qty * (Number(line.unitCost) || 0);
    });
    return { quantity: roundQty(quantity), cost: round2(cost) };
  }, [formData.items]);

  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "companyId") {
      handleCompanyChange(value);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /**
   * Switching company drops the lines that belong to a different one, because
   * they are no longer selectable and would otherwise be submitted unseen.
   * Lines that survive are kept - retyping quantities the user already entered
   * for the right company would be gratuitous.
   */
  const handleCompanyChange = (companyId) => {
    setFormData((prev) => {
      // Clearing the company leaves nothing selectable at all, so the lines go
      // with it rather than sitting there unverifiable.
      const kept = companyId
        ? prev.items.filter((line) => {
            if (!line.itemId) return true;
            return companyIdOf(itemsById[line.itemId]) === companyId;
          })
        : [];
      return {
        ...prev,
        companyId,
        items: kept.length ? kept : [emptyLine()],
      };
    });
    // Dropping lines renumbers the ones after them, so the line errors no
    // longer point at the right row. Errors on the header fields still do.
    setErrors((prev) => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!key.startsWith("items.")) next[key] = prev[key];
      });
      return next;
    });
  };

  const patchLine = (index, patch) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((line, i) =>
        i === index ? { ...line, ...patch } : line,
      ),
    }));
    setErrors((prev) => ({
      ...prev,
      [`items.${index}.itemId`]: "",
      [`items.${index}.quantity`]: "",
    }));
  };

  const handleItemChange = (index, itemId) => {
    const item = itemsById[itemId];
    patchLine(index, {
      itemId,
      itemName: item?.name || "",
      hsCode: item?.HSCodeId?.pctCode || "",
      uom: unitOf(item),
      // The item's own price is the starting point for what it cost to bring
      // in. It is only a default - a supplier's bill overrides it, so the field
      // stays editable.
      unitCost: priceOf(item),
    });
  };

  const addLine = () =>
    setFormData((prev) => ({ ...prev, items: [...prev.items, emptyLine()] }));

  const removeLine = (index) =>
    setFormData((prev) => ({
      ...prev,
      // Never leave the form with no lines - a receiving without one cannot be
      // saved, so an empty row is friendlier than an empty list.
      items:
        prev.items.length === 1
          ? [emptyLine()]
          : prev.items.filter((_, i) => i !== index),
    }));

  const validate = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = "This field is required";
    // Items hang off the company, so there is no usable form without one.
    if (!formData.companyId) newErrors.companyId = "This field is required";
    if (!formData.supplierName?.toString().trim()) {
      newErrors.supplierName = "This field is required";
    }

    const seen = new Set();
    formData.items.forEach((line, index) => {
      if (!line.itemId) {
        newErrors[`items.${index}.itemId`] = "This field is required";
      } else if (seen.has(line.itemId)) {
        newErrors[`items.${index}.itemId`] =
          "This item is already on another line";
      }
      if (line.itemId) seen.add(line.itemId);
      // Negated so a half-typed "." (Number(".") is NaN) fails too.
      if (!(Number(line.quantity) > 0)) {
        newErrors[`items.${index}.quantity`] = "Must be greater than 0";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
          firstError.focus();
        }
      }, 100);
      return;
    }

    onSubmit({
      receivingNumber: formData.receivingNumber,
      date: formData.date,
      supplierName: formData.supplierName,
      referenceNo: formData.referenceNo,
      note: formData.note,
      ...(formData.companyId ? { companyId: formData.companyId } : {}),
      items: formData.items.map((line) => ({
        // Keeping the line's own _id is what lets the ledger reconcile against
        // the same line instead of reversing it and adding it back.
        ...(line._id ? { _id: line._id } : {}),
        itemId: line.itemId,
        itemName: line.itemName,
        hsCode: line.hsCode,
        uom: line.uom,
        quantity: Number(line.quantity) || 0,
        unitCost: Number(line.unitCost) || 0,
      })),
    });
  };

  return (
    <div className="w-full min-h-screen flex justify-center py-4">
      <form
        onSubmit={handleSubmit}
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            color: globalStyle.curveDarkColor,
          }}
        >
          {submitLabel}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">Receiving No.</label>
            <input
              type="text"
              value={formData.receivingNumber || "Generated on save"}
              readOnly
              className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-600"
            />
          </div>

          <InputField
            label="Date"
            name="date"
            type="date"
            value={formData.date}
            error={errors.date}
            onChange={handleChange}
            max={today()}
            required
          />

          <InputField
            label="Supplier / Vendor"
            name="supplierName"
            value={formData.supplierName}
            error={errors.supplierName}
            onChange={handleChange}
            required
          />

          <InputField
            label="Reference / Bill No."
            name="referenceNo"
            value={formData.referenceNo}
            error={errors.referenceNo}
            onChange={handleChange}
          />

          <div>
            <label className="block mb-1 font-medium">
              Company<span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.companyId ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select</option>
              {companiesList.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.companyName}
                </option>
              ))}
            </SearchableSelect>
            {errors.companyId && (
              <p className="text-red-500 text-sm mt-1">{errors.companyId}</p>
            )}
          </div>

          <InputField
            label="Note"
            name="note"
            value={formData.note}
            error={errors.note}
            onChange={handleChange}
          />
        </div>

        {/* ---- lines ---- */}
        <div>
          <h3
            className="font-bold mb-3"
            style={{ color: globalStyle.curveDarkColor }}
          >
            Items Received
          </h3>

          {receivableItems.length === 0 && (
            <p className="text-sm text-gray-500 mb-3">
              {!formData.companyId
                ? "Select a company first - the items you can receive are that company's."
                : "This company has no stock-tracked products. Pick another company, or add a complete product for it first."}
            </p>
          )}

          {formData.items.map((line, index) => {
            const item = itemsById[line.itemId];
            const options = optionsFor(line.itemId);
            const state = itemStockState(item);
            const chip = state ? stockChipStyle(state) : null;
            return (
              <div
                key={line._key || line._id || index}
                className="border border-gray-200 rounded-lg p-4 mb-3 bg-gray-50"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <b>Item {index + 1}</b>
                  <Tooltip title="Remove line">
                    <IconButton size="small" onClick={() => removeLine(index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block mb-1 font-medium">
                      Item<span className="text-red-500">*</span>
                    </label>
                    <SearchableSelect
                      name={`items.${index}.itemId`}
                      value={line.itemId}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      disabled={options.length === 0}
                      className={`w-full px-4 py-2 border rounded-md ${
                        errors[`items.${index}.itemId`]
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="">Select</option>
                      {options.map((option) => (
                        <option key={option._id} value={option._id}>
                          {option.name}
                          {" — stock "}
                          {formatQty(option.currentStock, unitOf(option))}
                        </option>
                      ))}
                    </SearchableSelect>
                    {errors[`items.${index}.itemId`] && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors[`items.${index}.itemId`]}
                      </p>
                    )}
                    {chip && (
                      <span
                        className="inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: chip.bg,
                          color: chip.fg,
                        }}
                      >
                        {formatQty(item.currentStock, unitOf(item))} ·{" "}
                        {chip.label}
                      </span>
                    )}
                  </div>

                  <InputField
                    label="Quantity"
                    name={`items.${index}.quantity`}
                    type="number"
                    value={line.quantity}
                    error={errors[`items.${index}.quantity`]}
                    onChange={(e) =>
                      patchLine(index, { quantity: e.target.value })
                    }
                    min={0}
                    step="0.001"
                    required
                  />

                  <InputField
                    label="Unit Cost"
                    name={`items.${index}.unitCost`}
                    type="number"
                    value={line.unitCost}
                    onChange={(e) =>
                      patchLine(index, { unitCost: e.target.value })
                    }
                    min={0}
                    step="0.01"
                  />
                </div>

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 text-sm">
                  <span className="text-gray-500">
                    {line.uom ? `Unit: ${line.uom}` : ""}
                  </span>
                  <b>
                    Rs{" "}
                    {formatTwoDecimals(
                      (Number(line.quantity) || 0) *
                        (Number(line.unitCost) || 0),
                    )}
                  </b>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={addLine}
            // Every receivable item is already on a line, so a new one could
            // only ever sit there empty.
            disabled={optionsFor("").length === 0}
            className="flex items-center gap-1 px-4 py-2 rounded-md border border-dashed border-blue-400 text-blue-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AddIcon fontSize="small" /> Add Item
          </button>
        </div>

        <div className="flex justify-end gap-8 border-t pt-4">
          <div>
            <div className="text-gray-500 text-sm">Total Quantity</div>
            <div className="font-bold text-lg">
              {formatQty(totals.quantity)}
            </div>
          </div>
          <div>
            <div className="text-gray-500 text-sm">Total Cost</div>
            <div className="font-bold text-lg">
              Rs {formatTwoDecimals(totals.cost)}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full md:w-1/3 mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
          style={{ background: globalStyle.buttonColor }}
        >
          {submitting ? "Please Wait..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export { ITEM_TYPE };
export default ReceivingForm;
